import {
  EventData,
  Milestone,
  UserMilestoneProgress,
} from '@wwe-portal/entities/rbe.type';
import Image from 'next/image';
import { FC, useState } from 'react';
import { ClickableDiv } from '@wwe-portal/ui/components/button/clickable-div';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import { useTranslations } from 'next-intl';
import styles from './milestone-reward-item.module.css';
import { formatThousands } from '@wwe-portal/utils/format';

import { cn } from '@wwe-portal/ui/lib/lib';
import { useMilestoneRewardItem } from './use-milestone-reward-item';
import { ClaimButton } from './claim-button';
import {
  sendRBEMilestoneInfoHovered,
  sendRBERewardsCardInfoClick,
} from '@wwe-portal/web/libs/analytics/rbe';

const getRewardImage = (imageUrl: string) => {
  const parsedImageUrl = JSON.parse(imageUrl);
  return parsedImageUrl.url;
};

const MoreInfoIconButton: FC<{
  onClick: () => void;
  event: EventData;
  milestoneId: string;
}> = ({ onClick, event, milestoneId }) => {
  const handleClickMoreInfo = () => {
    onClick();
    sendRBERewardsCardInfoClick(event, milestoneId);
  };
  return (
    <ClickableDiv
      onClick={handleClickMoreInfo}
      onMouseEnter={() => {
        sendRBEMilestoneInfoHovered(event, milestoneId);
      }}
      className="border-text700 flex items-center justify-center rounded-md border p-[7px]"
    >
      <IcoMoonIcon
        iconName="information-fill"
        className="text-text600 size-4"
      />
    </ClickableDiv>
  );
};

interface MilestoneRewardProps {
  event: EventData;
  milestoneId: string;
  data: Milestone;
  userProgress: UserMilestoneProgress | undefined;
  totalPoints: number | undefined;
}

export const MilestoneRewardItem: FC<MilestoneRewardProps> = ({
  event,
  milestoneId,
  data,
  userProgress,
  totalPoints,
}) => {
  const { status, showMoreInfo, claim, isPending, top3Rewards, requirement } =
    useMilestoneRewardItem({
      event,
      milestoneId,
      data,
      userProgress,
      totalPoints,
    });

  return (
    <div
      aria-label={`Milestone ${data.level}`}
      className={cn(
        'bg-back300 relative flex h-[100px] items-stretch gap-2.5 rounded-xl p-2.5',
        { 'border-green700 border': status === 'claimed' }
      )}
    >
      <div className="relative w-1/3">
        <Image
          fill
          src="/assets/images/limited-time-events/milestones-shining-background.webp"
          alt=""
          className="object-cover opacity-15"
        />
        <div className="absolute inset-0 flex items-center justify-center gap-0.5 px-1">
          {top3Rewards.map((reward) => {
            return (
              <div className="h-10" key={reward.itemId}>
                <Image
                  className="h-10 object-contain"
                  src={getRewardImage(reward.imageUrl)}
                  alt={reward.displayName}
                  aria-label={reward.displayName}
                  width={40}
                  height={40}
                />
                <div className="text-2xs flex items-center justify-center">
                  x{reward.quantity}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="w-2/3">
        <div className="flex h-10 items-center justify-start text-sm">{`Earn ${requirement} Points`}</div>
        <div className="flex gap-2.5">
          <ClaimButton
            status={status}
            onClick={claim}
            isLoading={isPending}
            className="flex-1"
            requiredPoint={requirement}
            totalPoints={totalPoints}
          />
          <MoreInfoIconButton
            milestoneId={milestoneId}
            event={event}
            onClick={showMoreInfo}
          />
        </div>
      </div>
      {status === 'claimed' && (
        <IcoMoonIcon
          iconName="checkbox-rounded"
          className="text-green700 absolute right-2 top-2 h-4 w-4"
        />
      )}
      <div className="absolute left-1 top-1 h-4 w-4">
        {status === 'claimed' && (
          <Image
            src="/assets/images/limited-time-events/milestone-rewards-badge.svg"
            alt="milestone trophy"
            width={16}
            height={16}
            className="absolute inset-0"
          />
        )}

        <span
          className={cn(
            'absolute inset-0 flex h-4 w-4 items-center justify-center rounded-full text-xs font-semibold',
            {
              'bg-back400 text-text500': status !== 'claimed',
              'text-text200 bg-transparent': status === 'claimed',
            }
          )}
        >
          {data.level}
        </span>
      </div>
    </div>
  );
};

const rewardItemWidths = {
  1: 100,
  2: 76,
  3: 50,
};

export const MilestoneRewardCard: FC<MilestoneRewardProps> = ({
  event,
  milestoneId,
  data,
  userProgress,
  totalPoints,
}) => {
  const t = useTranslations();
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const handleShowMoreInfo = () => {
    setShowMoreInfo(true);
  };
  const handleCloseMoreInfo = () => {
    setShowMoreInfo(false);
  };
  const { status, top3Rewards, requirement, claim, isPending } =
    useMilestoneRewardItem({
      event,
      milestoneId,
      data,
      userProgress,
      totalPoints,
    });
  const numTop3Rewards = top3Rewards.length;
  return (
    <div className="relative overflow-hidden rounded-xl">
      <div
        className={cn({
          [styles.blur!]: showMoreInfo,
        })}
      >
        <div className="bg-back200 relative h-[200px]">
          <Image
            fill
            src="/assets/images/limited-time-events/milestones-shining-background.webp"
            alt=""
            className="object-cover opacity-15"
          />
          <div className="absolute inset-0 flex items-center justify-center gap-2.5 px-1">
            {top3Rewards.map((reward) => {
              return (
                <div className="" key={reward.itemId}>
                  <Image
                    className={cn(
                      'h-[125px] object-contain object-center md:w-[100px]',
                      {
                        'md:w-[50px]': numTop3Rewards > 2,
                        'md:w-[76px]': numTop3Rewards === 2,
                        'md:w-[100px]': numTop3Rewards === 1,
                      }
                    )}
                    src={getRewardImage(reward.imageUrl)}
                    alt={reward.displayName}
                    aria-label={reward.displayName}
                    width={rewardItemWidths[numTop3Rewards] ?? 50}
                    height={125}
                  />
                  <div className="flex items-center justify-center text-sm">
                    x{reward.quantity}
                  </div>
                </div>
              );
            })}
          </div>
          {status === 'claimed' && (
            <IcoMoonIcon
              iconName="checkbox-rounded"
              className="text-green700 absolute right-2 top-2 h-8 w-8"
            />
          )}
          <div className="absolute left-2 top-2 h-4 w-4 xl:h-8 xl:w-8">
            {status === 'claimed' && (
              <Image
                src="/assets/images/limited-time-events/milestone-rewards-badge.svg"
                alt="milestone trophy"
                fill
              />
            )}
            <span
              className={cn(
                'absolute inset-0 flex items-center justify-center rounded-full text-xs font-semibold',
                {
                  'bg-back400 text-text500': status !== 'claimed',
                  'text-text200 bg-transparent': status === 'claimed',
                }
              )}
            >
              {data.level}
            </span>
          </div>
        </div>
        <div className="bg-back600 p-5">
          <div className="text-text500 mb-2.5 text-sm">
            {`Earn ${requirement} Points`}
          </div>
          <div className="flex items-center justify-between">
            <ClaimButton
              status={status}
              onClick={claim}
              isLoading={isPending}
              className="w-[138px]"
              requiredPoint={requirement}
              totalPoints={totalPoints}
            />
            <MoreInfoIconButton
              milestoneId={milestoneId}
              event={event}
              onClick={handleShowMoreInfo}
            />
          </div>
        </div>
      </div>
      <div
        className={cn(styles.hoverStateBlur, {
          [styles.show!]: showMoreInfo,
        })}
      >
        <div className={styles.hoverState}>
          <div
            data-id="milestones-detail-container"
            className="bg-bgColor26 relative h-full w-full rounded-xl px-4 py-6"
          >
            <ClickableDiv
              className="absolute right-0 top-0 p-3"
              onClick={handleCloseMoreInfo}
            >
              <IcoMoonIcon iconName="close" className="text-text700 size-3" />
            </ClickableDiv>
            <p className="m-0 pb-5 text-sm font-medium">{t('rewards')}</p>
            <ul
              className={cn(
                'text-textColor2 scrollbar-moz flex list-inside flex-col gap-2.5 px-0 text-xs',
                styles.hoverStateList
              )}
            >
              {data.rewards.map((reward) => (
                <li key={reward?.itemId}>
                  {formatThousands(reward.quantity)}{' '}
                  {reward.displayName || reward.itemId}
                </li>
              ))}
            </ul>
            <div className="absolute bottom-3 left-3">
              <ClaimButton
                status={status}
                onClick={claim}
                isLoading={isPending}
                className="w-[138px]"
                requiredPoint={requirement}
                totalPoints={totalPoints}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
